// Fork of product-form.js for the Fortinet quotation product page
// (sections/main-product-fortinet.liquid). Variant selection, default-variant
// pre-selection and live price updates are kept. The add-to-basket submission
// is gone: there is no cart form on this layout, the primary button always
// reads "Request a Quotation" and links straight to /pages/request-a-quote.
// Every lookup is guarded so the script cannot crash when [data-add-to-cart]
// or any other cart-era hook is absent.
document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('product-form');
  if (!form) return;

  const QUOTE_LABEL = 'Request a Quotation';
  const AVAILABILITY_LABEL = 'Request availability';

  const variantInput = form.querySelector('[data-variant-id]');
  // The quote layout has no add-to-basket button; the reference stays nullable
  // so shared logic keeps working if a block ever reintroduces one.
  const addButton = form.querySelector('[data-add-to-cart]');
  const buyButton = form.querySelector('[data-quote-cta]');
  const optionSelectors = Array.from(form ? form.querySelectorAll('[data-option-selector]') : []);
  const clearVariantButton = form ? form.querySelector('[data-clear-variant]') : null;
  const quantityInput = form ? form.querySelector('[name="quantity"]') : null;
  const priceDisplay = document.querySelector('[data-product-price]');
  const priceMain = priceDisplay?.querySelector('[data-price-main]');
  const stickyCart = document.querySelector('[data-sticky-cart]');
  const stickyButton = document.querySelector('[data-sticky-add]');
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const stickyStock = document.querySelector('[data-sticky-stock]');
  let currentVariant = optionSelectors.length ? null : undefined;

  function getVariants() {
    if (!window.productVariants) {
      window.productVariants = JSON.parse(document.getElementById('product-variants-json')?.textContent || '[]');
    }
    return window.productVariants;
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }

    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format((Number(cents) || 0) / 100);
  }

  function customerPrice(cents) {
    const price = Number(cents) || 0;
    return priceDisplay?.dataset.reseller === 'true' ? Math.round(price * 0.95) : price;
  }

  function priceRangeText() {
    if (!priceDisplay) return '';
    const min = customerPrice(priceDisplay.dataset.priceMin);
    const max = customerPrice(priceDisplay.dataset.priceMax);
    return min === max ? formatMoney(min) : `${formatMoney(min)} - ${formatMoney(max)}`;
  }

  // Price-on-request products never render a numeric price and must never
  // have one written back in by a variant change - picking "Hardware +
  // Enterprise Protection" would otherwise silently overwrite "Price on
  // request" with a real-looking (but meaningless, since the SKU has no
  // list price at all) £0.00.
  const priceOnRequest = form?.dataset.priceOnRequest === 'true';

  function setPriceText(text) {
    if (priceOnRequest) return;
    if (priceMain) priceMain.textContent = text;
    if (stickyPrice) stickyPrice.textContent = text;
  }

  // Quotation pages never sell out and never disable the button: an
  // unavailable variant simply asks for availability inside the quote instead.
  function updateButtons(variant) {
    const label = variant && !variant.available ? AVAILABILITY_LABEL : QUOTE_LABEL;
    [buyButton, stickyButton, addButton].forEach((button) => {
      if (!button) return;
      button.disabled = false;
      button.textContent = label;
    });
  }

  function clearVariantSelection() {
    optionSelectors.forEach(select => {
      select.value = '';
    });
    showPendingVariantState();
    if (clearVariantButton) clearVariantButton.hidden = true;
  }

  function showPendingVariantState() {
    currentVariant = null;
    if (variantInput) variantInput.value = '';
    setPriceText(priceRangeText());
    updateButtons(null);
    if (stickyStock) stickyStock.textContent = 'Choose a bundle';
  }

  const stickyAnchor = buyButton || addButton;

  function updateStickyCartVisibility() {
    if (!stickyCart || !stickyAnchor) return;

    const buttonRect = stickyAnchor.getBoundingClientRect();
    const header = document.querySelector('[data-sticky-header], .shopify-section-header-sticky, .header-wrapper, .site-header, .header');
    const headerOffset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    const buttonAboveViewport = buttonRect.bottom <= headerOffset;
    const buttonVisible = buttonRect.bottom > headerOffset && buttonRect.top < window.innerHeight;
    const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
    const shouldShow = isMobileViewport
      ? !buttonVisible
      : window.scrollY > 0 && buttonAboveViewport && !buttonVisible;

    stickyCart.classList.toggle('is-visible', shouldShow);
    stickyCart.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  if (stickyCart && stickyAnchor) {
    stickyCart.setAttribute('aria-hidden', 'true');

    if ('IntersectionObserver' in window) {
      const stickyObserver = new IntersectionObserver(updateStickyCartVisibility, {
        root: null,
        threshold: 0
      });
      stickyObserver.observe(stickyAnchor);
    }

    window.addEventListener('scroll', updateStickyCartVisibility, { passive: true });
    window.addEventListener('resize', updateStickyCartVisibility);
    updateStickyCartVisibility();
  }

  function getSelectedVariant() {
    const options = optionSelectors.map(s => s.value);
    if (options.some(option => !option)) return null;
    return getVariants().find(v => {
      if (v.options.length !== options.length) return false;
      return v.options.every((opt, i) => opt === options[i]);
    });
  }

  // The first available variant starts selected, so a product with bundles
  // opens showing a price instead of demanding a choice before quoting.
  // The Clear button still returns the picker to its unselected state.
  function selectDefaultVariant() {
    if (!optionSelectors.length) return;
    const variants = getVariants();
    const preferred = variants.find(v => v.available) || variants[0];
    if (!preferred) {
      clearVariantSelection();
      return;
    }
    optionSelectors.forEach((select, index) => {
      select.value = preferred.options[index] ?? '';
    });
    optionSelectors[0].dispatchEvent(new Event('change'));
  }

  optionSelectors.forEach(select => {
    select.addEventListener('change', () => {
      const variant = getSelectedVariant();
      if (variant) {
        currentVariant = variant;
        if (variantInput) variantInput.value = variant.id;
        setPriceText(formatMoney(customerPrice(variant.price)));
        updateButtons(variant);
        if (stickyStock) stickyStock.textContent = variant.available ? 'In stock' : 'Available to order';
        if (clearVariantButton) clearVariantButton.hidden = false;
      } else {
        showPendingVariantState();
        if (clearVariantButton) {
          clearVariantButton.hidden = !optionSelectors.some(optionSelector => optionSelector.value);
        }
      }
    });
  });

  if (clearVariantButton) {
    clearVariantButton.addEventListener('click', clearVariantSelection);
  }

  if (optionSelectors.length) {
    selectDefaultVariant();
  }
});
